import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import activoClassificationService from '../../../services/activoClassificationService';
import { getAreas } from '../../../services/areaService';

const emptyForm = {
  code: '',
  name: '',
  category_id: '',
  subcategory_id: '',
  activo_family_id: '',
  area_id: '',
  acquisition_quantity: '1',
  acquisition_unit_price: '',
  activity_start_date: '',
  useful_life_years: '',
  brand: '',
  model: '',
  serial_number: '',
  notes: '',
};

const ActivoInicialForm = ({ onSubmit, loading, submitLabel = 'Registrar activo inicial' }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [hierarchy, setHierarchy] = useState([]);
  const [areas, setAreas] = useState([]);
  const [formLoading, setFormLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      activoClassificationService.getHierarchy({ active_only: true }),
      getAreas({ active_only: true }),
    ])
      .then(([hierarchyRes, areasRes]) => {
        if (cancelled) return;
        if (hierarchyRes.success && Array.isArray(hierarchyRes.data)) {
          setHierarchy(hierarchyRes.data);
        }
        if (areasRes.success && Array.isArray(areasRes.data)) {
          setAreas(areasRes.data);
        }
      })
      .finally(() => {
        if (!cancelled) setFormLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const subcategories = useMemo(() => {
    if (!formData.category_id) return [];
    const cat = hierarchy.find((c) => String(c.id) === String(formData.category_id));
    return cat?.subcategories || [];
  }, [hierarchy, formData.category_id]);

  const families = useMemo(() => {
    if (!formData.subcategory_id) return [];
    const sub = subcategories.find((s) => String(s.id) === String(formData.subcategory_id));
    return sub?.families || [];
  }, [subcategories, formData.subcategory_id]);

  const financePreview = useMemo(() => {
    const qty = parseFloat(formData.acquisition_quantity || 0);
    const price = parseFloat(formData.acquisition_unit_price || 0);
    const years = parseInt(formData.useful_life_years, 10);
    const base = qty * price;
    if (!base || base <= 0 || !years || years < 1) return null;
    const monthly = base / (years * 12);
    return { base, monthly, annual: base / years };
  }, [formData.acquisition_quantity, formData.acquisition_unit_price, formData.useful_life_years]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'category_id') {
        next.subcategory_id = '';
        next.activo_family_id = '';
      }
      if (name === 'subcategory_id') {
        next.activo_family_id = '';
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      code: formData.code.trim(),
      name: formData.name.trim(),
      activo_family_id: parseInt(formData.activo_family_id, 10),
      area_id: parseInt(formData.area_id, 10),
      acquisition_quantity: parseFloat(formData.acquisition_quantity || 1),
      acquisition_unit_price: parseFloat(formData.acquisition_unit_price),
      acquisition_currency: 'PEN',
      activity_start_date: formData.activity_start_date,
      useful_life_years: parseInt(formData.useful_life_years, 10),
      brand: formData.brand.trim() || null,
      model: formData.model.trim() || null,
      serial_number: formData.serial_number.trim() || null,
      notes: formData.notes.trim() || null,
    });
  };

  if (formLoading) {
    return <div className="text-sm text-gray-500 py-6">Cargando formulario…</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Código *</label>
          <input
            type="text"
            name="code"
            value={formData.code}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Nombre *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Categoría *</label>
          <select
            name="category_id"
            value={formData.category_id}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">Seleccione…</option>
            {hierarchy.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Subcategoría *</label>
          <select
            name="subcategory_id"
            value={formData.subcategory_id}
            onChange={handleChange}
            required
            disabled={!formData.category_id}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
          >
            <option value="">Seleccione…</option>
            {subcategories.map((s) => (
              <option key={s.id} value={s.id}>{s.code} — {s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Familia *</label>
          <select
            name="activo_family_id"
            value={formData.activo_family_id}
            onChange={handleChange}
            required
            disabled={!formData.subcategory_id}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
          >
            <option value="">Seleccione…</option>
            {families.map((f) => (
              <option key={f.id} value={f.id}>{f.code} — {f.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Área *</label>
          <select
            name="area_id"
            value={formData.area_id}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">Seleccione…</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
            ))}
          </select>
          {areas.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              Configure áreas en{' '}
              <Link to="/activos/configuracion/areas" className="underline">Activos → Configuración</Link>.
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Cantidad</label>
          <input
            type="number"
            name="acquisition_quantity"
            min="0.0001"
            step="any"
            value={formData.acquisition_quantity}
            onChange={handleChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Precio unitario sin IGV *</label>
          <input
            type="number"
            name="acquisition_unit_price"
            min="0"
            step="0.01"
            value={formData.acquisition_unit_price}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Fecha inicio actividades *</label>
          <input
            type="date"
            name="activity_start_date"
            value={formData.activity_start_date}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Vida útil (años) *</label>
          <input
            type="number"
            name="useful_life_years"
            min="1"
            max="100"
            value={formData.useful_life_years}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Marca</label>
          <input type="text" name="brand" value={formData.brand} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Modelo</label>
          <input type="text" name="model" value={formData.model} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">N° serie</label>
          <input type="text" name="serial_number" value={formData.serial_number} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
          <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md" />
        </div>
      </div>

      {financePreview && (
        <div className="rounded-md border border-teal-200 bg-teal-50/60 px-3 py-2 text-xs text-teal-900 flex flex-wrap gap-x-4 gap-y-1">
          <span>Costo s/IGV: <strong>S/ {financePreview.base.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</strong></span>
          <span>Cuota mensual ref.: <strong>S/ {financePreview.monthly.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</strong></span>
          <span>Cuota anual: <strong>S/ {financePreview.annual.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</strong></span>
        </div>
      )}

      <div className="flex justify-end pt-2 border-t border-gray-100">
        <button
          type="submit"
          disabled={loading}
          className="px-5 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium disabled:opacity-50"
        >
          {loading ? 'Guardando…' : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default ActivoInicialForm;
